import React, { Fragment } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isEmpty } from 'lodash-es';
import InfiniteScroll from 'react-infinite-scroll-component';
import { TransitionMotion, spring, presets } from 'react-motion';

import EmptyRow from './EmptyRow';
import LoadingSpinner from '../../LoadingSpinner';

const TBody = ({
  tableRows,
  isTableLayout,
  isAnimatedTable,
  isInfiniteScroll,
  infiniteScrollProps,
  animatedTableProps,
  emptyCellColSpan,
  emptyState,
  className,
  style,
}) => {
  const showEmptyState = !isEmpty(emptyState) && tableRows.length === 0;

  const renderEmptyRow = () => (
    <EmptyRow
      isTableLayout={isTableLayout}
      emptyCellColSpan={emptyCellColSpan}
      emptyState={emptyState}
    />
  );

  // Default Layout
  const renderDefaultLayout = () => (
    <tbody className={className} style={style} ref={infiniteScrollProps.tableBodyRef}>
      {showEmptyState ? renderEmptyRow() : tableRows}
    </tbody>
  );

  // Infinite Scroll Layout
  const renderInfiniteScrollLayout = () => {
    const {
      onPageChange = () => {},
      dataLength = 10,
      hasMore = true,
      limit = 10,
      offset = 0,
      total = 10,
      finishText = 'No more found',
      containerId = 'show-scrollbar',
      outerContainerId,
      tableMaxheight = 500,
      tableScrollRef = null,
      hasScrollbarClass = true,
      onScroll = () => {},
    } = infiniteScrollProps;

    return (
      <div className={cx('d-table d-tbody', className)} style={style}>
        <div
          style={{
            width: style.width,
            maxHeight: tableMaxheight,
            overflowX: 'hidden',
            display: 'table-caption',
           //  borderRight: '1px solid #e1e5eb',
          }}
          className={cx(hasScrollbarClass ? 'show-scrollbar' : '')}
          id={containerId}
          ref={tableScrollRef}
          onScroll={onScroll}
        >
          <InfiniteScroll
            dataLength={dataLength}
            next={() => hasMore && onPageChange({ limit, offset })}
            hasMore={hasMore}
            loader={total && <LoadingSpinner id={`loading-spinner-${dataLength}`} />}
            scrollThreshold={0.99}
            scrollableTarget={outerContainerId || containerId}
          >
            {showEmptyState ? renderEmptyRow() : tableRows}

            {tableRows.length > 0 && parseInt(total, 10) === dataLength && (
              <div className="d-flex w-100 noMoreRows">
                <div className="d-flex justify-content-center align-items-center text-center d-td" style={{ height: 93 }} >
                  <p className="text-16 text-center">{finishText}</p>
                </div>
              </div>
            )}
          </InfiniteScroll>
        </div>
      </div>
    );
  };

  // Animated Layout
  const rowWithStyles = (row, styles) => row.map((value, key) => ({
    key: `${key}`,
    data: value,
    style: styles,
  }));
  const willEnter = () => ({ maxHeight: 0, opacity: 1 });
  const willLeave = () => ({ maxHeight: spring(0), opacity: spring(0) });

  const renderAnimatedLayout = () => {
    const { trMaxHeight = 60 } = animatedTableProps;
    return (
      <TransitionMotion
        defaultStyles={rowWithStyles(tableRows, { maxHeight: trMaxHeight, opacity: 1 })}
        styles={rowWithStyles(tableRows, {
          maxHeight: spring(trMaxHeight, presets.stiff),
          opacity: spring(1, presets.stiff),
        })}
        willLeave={willLeave}
        willEnter={willEnter}
      >
        {interpolatedStyles => (
          <div className={cx('d-table d-tbody', className)} style={style}>
            {showEmptyState ? renderEmptyRow() : interpolatedStyles.map(
              ({ key, data, style: styles }) => (
                <div key={`${key}`} className={cx('d-table d-tr')} style={styles}>{data}</div>
            ))}
          </div>
        )}
      </TransitionMotion>
    );
  };

  return (
    <Fragment>
      {isTableLayout && renderDefaultLayout()}
      {isInfiniteScroll && renderInfiniteScrollLayout()}
      {isAnimatedTable && renderAnimatedLayout()}
    </Fragment>
  );
};

TBody.propTypes = {
  tableRows: T.array.isRequired,
  isTableLayout: T.bool,
  isAnimatedTable: T.bool,
  isInfiniteScroll: T.bool,
  infiniteScrollProps: T.shape({
    containerId: T.string,
    outerContainerId: T.string,
    finishText: T.string,
    onPageChange: T.func,
    onScroll: T.func,
    dataLength: T.number,
    offset: T.number,
    total: T.number,
    limit: T.number,
    hasMore: T.bool,
    hasScrollbarClass: T.bool,
    tableMaxheight: T.oneOfType([T.number, T.string]),
    tableScrollRef: T.oneOfType([T.func, T.shape({ current: T.any })]),
  }),
  animatedTableProps: T.shape({
    trMaxHeight: T.number,
  }),
  emptyState: T.oneOfType([T.string, T.node]),
  emptyCellColSpan: T.number,
  className: T.string,
  style: T.object,
};


TBody.defaultProps = {
  isTableLayout: false,
  isAnimatedTable: false,
  isInfiniteScroll: false,
  emptyState: '',
  infiniteScrollProps: {},
  animatedTableProps: {},
};

export default TBody;
